import React, {useEffect, useMemo, useState} from 'react';
import {CasinoSelectItem} from '@/common-pages/casino/casino-select';
import {
  getGameCategory,
  getGameProvider,
  NewGameListItem,
  NewGameListParams,
  postNewGameList,
} from '@/common-pages/casino/casino.service';
import {FilterModalOption} from '@basicComponents/filter-modal';
import {sortBy} from '@/common-pages/live-casino/live-casino-service';
import {normalizeGamePush} from '@/utils/game';
import {GamePicType, getGamePicUrl} from '@/components/utils';

const userIcon = require('@/assets/icons/home/user.webp');

type AnnaGamesTab = {
  label: string;
  value: string;
};

type AnnaGamesItem = NewGameListItem & {
  playersNumber?: number;
};

const AnnaHomeGames = () => {
  const [topTabs, setTopTabs] = useState<AnnaGamesTab[]>([]);
  const [providerOptions, setProviderOptions] = useState<FilterModalOption[]>([
    {
      title: 'All',
      value: '',
    },
  ]);
  const [gameType, setGameType] = useState('');
  const [provider, setProvider] = useState('');
  const [sort, setSort] = useState('');
  const [gameList, setGameList] = useState<AnnaGamesItem[]>([]);

  const sortOptions = useMemo(
    () =>
      sortBy.map(item => ({
        title: item.label,
        value: item.value,
      })),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    getGameCategory('casino')
      .then(res => {
        if (cancelled) {
          return;
        }

        const nextTabs = res.map(item => ({
          label: item.name,
          value: String(item.id),
        }));

        setTopTabs(nextTabs);
        setGameType(currentValue => currentValue || nextTabs[0]?.value || '');
      })
      .catch(() => undefined);

    getGameProvider('casino')
      .then(res => {
        if (cancelled) {
          return;
        }

        setProviderOptions([
          {
            title: 'All',
            value: '',
          },
          ...res.map(item => ({
            title: item.name,
            value: String(item.id),
          })),
        ]);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!topTabs.length || !gameType) {
      return;
    }

    let cancelled = false;
    const params: NewGameListParams = {
      l1CategoryId: 2,
      l2CategoryId: gameType,
      pageNo: 1,
      pageSize: 30,
    };

    if (provider) {
      params.providerId = provider;
    }

    if (sort) {
      params.orderByField = 'name';
      params.isAsc = sort === 'A-Z';
    }

    postNewGameList(params)
      .then(res => {
        if (cancelled) {
          return;
        }

        setGameList((res.content || []) as AnnaGamesItem[]);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setGameList([]);
      });

    return () => {
      cancelled = true;
    };
  }, [gameType, provider, sort, topTabs.length]);

  return (
    <div>
      <div className="flex h-12 min-h-12 flex-col justify-center bg-[linear-gradient(180deg,#FFF_0%,rgba(255,255,255,0)_100%)] px-3">
        <div className="relative flex h-full w-full flex-row flex-nowrap items-center justify-start gap-x-1 overflow-x-auto bg-transparent p-0">
          {topTabs.map(item => {
            const isActive = item.value === gameType;

            return (
              <button
                key={item.value}
                type="button"
                onClick={() => setGameType(item.value)}
                className="group relative z-0 flex h-8 w-auto flex-none cursor-pointer items-center justify-center rounded-none px-0 py-1 transition-opacity">
                {isActive ? (
                  <span className="absolute bottom-0 z-0 h-1 w-4 rounded-none bg-[#E91100] shadow-[0_1px_0px_0_rgba(0,0,0,0.05)]" />
                ) : null}
                <div className="relative z-10 whitespace-nowrap transition-colors">
                  <div className="flex flex-row gap-x-1 rounded bg-white px-2 py-1">
                    <span className="text-xs text-[#31373D]">{item.label}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex h-14 items-center justify-between bg-[linear-gradient(180deg,#FFFFFF_0%,#F6F7FA_100%)] px-3 py-3">
        <CasinoSelectItem
          optionsList={providerOptions}
          name="Categories"
          onChange={value => setProvider(String(value || ''))}
        />
        <CasinoSelectItem
          optionsList={sortOptions}
          name="Sort"
          onChange={value => setSort(String(value || ''))}
        />
      </div>

      <div className="flex flex-wrap px-3 pb-1.5">
        {gameList.map((game, index) => (
          <div
            key={game.id}
            className={`flex h-[9.625rem] w-[calc((100%-0.5rem)/2)] cursor-pointer flex-col overflow-hidden rounded-lg ${
              index % 2 === 0 ? 'mr-2' : ''
            } ${index > 1 ? 'mt-2' : ''}`}
            onClick={() => normalizeGamePush(game.id, true)}>
            <div
              className="flex-1 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${getGamePicUrl(
                  GamePicType.H_RECTANGLE,
                  game.source,
                  game.tripartiteUniqueness,
                )})`,
              }}
            />
            <div className="flex h-[2.5rem] items-center justify-between bg-white px-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold leading-none text-[#31373D]">
                  {game.name}
                </p>
                <p className="truncate text-[0.625rem] font-medium leading-none text-[#5F6975]">
                  {game.provider}
                </p>
              </div>
              {game.playersNumber ? (
                <div className="flex shrink-0 flex-col items-center">
                  <img src={userIcon} alt="" className="h-3 w-3" />
                  <span className="text-[0.625rem] font-medium leading-none text-[#31373D]">
                    {game.playersNumber}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnnaHomeGames;
